import { useId } from 'react';
import { journeyRows, type BoardPick, type JourneyChip } from '../logic/dashboard';

// TODO(witems): Chip forwards no aria-pressed/aria-label/disabled — see docs/witems-feedback.md.
interface AccessJourneyProps {
  filters?: unknown[];
  selected?: string[];
  selectedReady?: string[];
  userRole?: string | null;
  loading?: boolean;
  onPick: (pick: BoardPick) => void;
}

const SKELETON_SLOTS = ['onboarding', 'reporting', 'care'];

function countLabel(count: number): string {
  return count === 1 ? '1 patient' : `${count} patients`;
}

function Chip({ chip, on, onPick }: { chip: JourneyChip; on: boolean; onPick: (pick: BoardPick) => void }) {
  // A selected chip is never disabled, whatever its count, so it can always be un-pressed.
  const disabled = chip.count === 0 && !on;
  const late = chip.urgent && chip.count > 0;
  return (
    <button
      type="button"
      className={`journey-chip${on ? ' is-selected' : ''}${disabled ? ' is-empty' : ''}`}
      aria-pressed={on}
      aria-disabled={disabled || undefined}
      aria-label={`Filter roster: ${chip.label}, ${countLabel(chip.count)}${late ? ', past the deadline' : ''}`}
      onClick={() => { if (!disabled) onPick({ key: chip.pickKey, value: chip.pickValue }); }}
    >
      <span aria-hidden="true">{chip.emoji}</span>
      <strong className={`journey-chip__count${late ? ' is-late' : ''}`}>{chip.count}</strong>
      <span className="journey-chip__label">{chip.label}</span>
    </button>
  );
}

// What the care team has to do: onboarding steps, CMS reports, care and billing.
export default function AccessJourney({
  filters = [], selected = [], selectedReady = [], userRole, loading = false, onPick,
}: AccessJourneyProps) {
  const titleId = useId();
  const rows = loading ? [] : journeyRows(filters, userRole, selected, selectedReady);
  const isOn = (c: JourneyChip) => (c.pickKey === 'report_ready' ? selectedReady.includes(c.pickValue) : selected.includes(c.value));
  if (!loading && rows.length === 0) return null;
  return (
    <section className="board-section" aria-labelledby={titleId} aria-busy={loading || undefined}>
      <div className="board-section__head">
        <h2 className="board-section__title" id={titleId}>ACCESS journey</h2>
        <span className="board-section__note">what the care team has to do · a patient can be on several lines</span>
      </div>
      <div className="journey-rows">
        {loading && SKELETON_SLOTS.map((id) => (
          <div key={id} className="journey-row">
            <span className="skeleton-line journey-row__skeleton-label" />
            <span className="skeleton-block journey-row__skeleton" />
          </div>
        ))}
        {rows.map((r) => (
          <div key={r.id} className="journey-row" role="group" aria-label={r.label}>
            <span className="journey-row__label" aria-hidden="true">{r.label}</span>
            {r.chips.map((c) => <Chip key={c.value} chip={c} on={isOn(c)} onPick={onPick} />)}
          </div>
        ))}
      </div>
    </section>
  );
}
